/**
 * OverviewPanel — the demo's landing page.
 *
 * Mirrors chapter 1 §1.1.0 of the manuscript: the four incidents you
 * have probably seen, the binary view's failure, the typed refusal.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";

interface Incident {
  readonly id: string;
  readonly title: string;
  readonly story: string;
  readonly sdp: string;
  readonly refusedBy: string;
  readonly accent: string;
}

const INCIDENTS: readonly Incident[] = [
  {
    id: "coder-deploy",
    title: "Coding-agent fleet — delegation reused at the wrong seam",
    story:
      "A Planner delegates file-edit authority to a Coder. An adversarial prompt convinces the Coder to present that delegation at the Deployer's seam. The token is well-formed and signed; the binary checker admits.",
    sdp: "DELEGATE (Dt) — η at the wrong scope",
    refusedBy: "T2",
    accent: "#ec4899",
  },
  {
    id: "mcp-replay",
    title: "MCP server confused deputy — read capability presented for writes",
    story:
      "An assistant connected to two MCP servers receives a read capability from S₁ and is induced (by a retrieved document's instructions) to present it at S₂'s write seam.",
    sdp: "PARTITION (Pa) — η at the wrong boundary",
    refusedBy: "T2",
    accent: "#14b8a6",
  },
  {
    id: "rag-lineage",
    title: "RAG lineage substitution — provenance label swap",
    story:
      "A retrieval pipeline carries chunks with provenance fields. Indirect prompt injection persuades the model to swap a chunk's provenance for an approved-source label and ship it downstream.",
    sdp: "TRACE (Tr) — ε with the wrong lineage",
    refusedBy: "T2",
    accent: "#22d3ee",
  },
  {
    id: "cicd-replay",
    title: "Autonomous CI/CD credential replay",
    story:
      "A deployment workflow holds a precommit token consumed on first deploy. A stale workflow cache re-presents the token on a later run; the production gate admits.",
    sdp: "REQUIRE (At) — consumed grade is 0_G",
    refusedBy: "T1",
    accent: "#a78bfa",
  },
];

export function OverviewPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Thesis"
      subtitle="FOUR INCIDENTS · ONE STRUCTURAL FIX · A TYPED REFUSAL OF THE CONFUSED-DEPUTY CLASS"
      accentColor="#22d3ee"
    >
      <PanelSurface.ThesisCard
        heading="What this demo claims, in one paragraph"
        color="#22d3ee"
        glowColor="#22d3ee44"
        quoteSource="Two-Categorical CSK thesis"
        quote={
          <>
            The Categorical Safety Kernel already refuses whole vulnerability
            classes by making the dangerous construction unconstructable. The{" "}
            <strong>Two-Categorical CSK</strong> contribution extends that
            refusal from <em>does a bridge exist?</em> to{" "}
            <em>does a bridge of the right kind exist?</em>. Every bridge across
            a seam carries a typed <strong>grade</strong> indexing which of
            seven Structural Duality Principle (SDP) adjunctions it claims to
            discharge. Bridges with the wrong grade are not detected and
            refused; they have <em>no inhabitant</em> at the seam where they
            would have to live. This demo is the live, interactive companion to
            that thesis.
          </>
        }
      />

      <PanelSurface.KpiGrid minColumnWidth={160} gap={12}>
        <PanelSurface.KpiBox
          label="SDPs"
          value={7}
          color="#ec4899"
          subtitle="Tr · Al · St · At · Dt · De · Pa"
        />
        <PanelSurface.KpiBox
          label="Grade components"
          value={14}
          color="#a78bfa"
          subtitle="2 phases per SDP · ℤ/2 each"
        />
        <PanelSurface.KpiBox
          label="Theorems"
          value="T1–T4"
          color="#22d3ee"
          subtitle="the confused-deputy refusals"
        />
        <PanelSurface.KpiBox
          label="Confused-deputy class"
          value="∅"
          color="#22c55e"
          subtitle="under graded typing"
        />
        <PanelSurface.KpiBox
          label="Runtime checks needed"
          value={0}
          color="#fbbf24"
          subtitle="refusal is at the type-checker"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>
          Four incidents you have probably already shipped against
        </PanelSurface.SectionHeading>
        <PanelSurface.Prose variant="caption" style={{ margin: "8px 0 14px" }}>
          Each row is a sanitized pattern drawn from production AI deployments
          in the last eighteen months. None of them involves a forged token; in
          every case the bridge body is well-formed. The binary seam view cannot
          distinguish them from legitimate crossings. The graded view refuses
          them at the type level.
        </PanelSurface.Prose>
        <PanelSurface.FeatureGrid
          ariaLabel="Confused-deputy incident patterns"
          variant="rows"
          items={INCIDENTS.map((incident) => ({
            id: incident.id,
            title: incident.title,
            description: incident.story,
            color: incident.accent,
            badges: [
              { label: incident.sdp, color: incident.accent },
              { label: `refused by ${incident.refusedBy}`, color: panelTokens.accentRed },
            ],
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>The shape of the contribution</PanelSurface.SectionHeading>
        <PanelSurface.FeatureGrid
          ariaLabel="Binary and graded bridge questions"
          minColumnWidth={260}
          items={[
            {
              id: "binary",
              eyebrow: "Binary (Q1 only)",
              title: "Does a bridge between A and B exist?",
              description: (
                <>
                  Yes iff a <PanelSurface.InlineCode>Bridge A B</PanelSurface.InlineCode> can be
                  constructed.
                </>
              ),
              color: panelTokens.muted,
            },
            {
              id: "graded",
              eyebrow: "Graded (Q1 + Q2)",
              title: "Does the bridge carry the required SDP-adjunction grade?",
              description: (
                <>
                  Yes iff <PanelSurface.InlineCode>DischargesAdjunctionGrade g b</PanelSurface.InlineCode>{" "}
                  is inhabited for the seam&apos;s grade{" "}
                  <PanelSurface.InlineCode>g</PanelSurface.InlineCode>.
                </>
              ),
              color: panelTokens.accentGreen,
            },
          ]}
        />
        <PanelSurface.Prose style={{ marginTop: 12 }}>
          <strong>
            Q2 has no inhabitant for the Tier-2 confused-deputy adversary.
          </strong>{" "}
          The contribution of this manuscript is that structural absence.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>How to use this demo</PanelSurface.SectionHeading>
        <PanelSurface.ContentList
          ariaLabel="Two-Categorical CSK demo reading path"
          variant="ordered"
          items={[
            {
              id: "sdps",
              title: "Seven SDPs",
              body: "Read the alphabet of grades; each SDP comes with a concrete AI scenario.",
              color: "#ec4899",
            },
            {
              id: "grade-monoid",
              title: "Grade Monoid",
              body: "See the Z/2-per-phase grade vector, click components to add them, watch Z/2 self-cancel.",
              color: "#a78bfa",
            },
            {
              id: "bridge-constructor",
              title: "Bridge Constructor",
              body: "The showpiece: build a bridge interactively and watch the type-checker decide.",
              color: "#22d3ee",
            },
            {
              id: "snf-walker",
              title: "SNF Walker",
              body: "Step through B1 -> B2 -> B3 -> B4 and observe T4 phase-index diagonality.",
              color: "#14b8a6",
            },
            {
              id: "composition",
              title: "Composition",
              body: "Chain two graded bridges, see Case A / B / C / D verdicts and ICG envelope detection.",
              color: "#38bdf8",
            },
            {
              id: "attack-simulator",
              title: "Attack Simulator",
              body: "Pick one of CD-1..CD-4 or a real AI scenario, then see binary admit vs graded refuse side by side.",
              color: "#ef4444",
            },
            {
              id: "theorems",
              title: "Theorems T1-T4",
              body: "The four candidates with statements, operational meaning, and adversarial calibration.",
              color: "#22c55e",
            },
            {
              id: "limits",
              title: "Limits & Scope",
              body: "What this discipline does and does not defend against; honest position in the AI safety stack.",
              color: panelTokens.muted,
            },
          ]}
        />
        <PanelSurface.Prose variant="note" style={{ marginTop: 14 }}>
          Use <PanelSurface.InlineCode>[</PanelSurface.InlineCode> and <PanelSurface.InlineCode>]</PanelSurface.InlineCode> to move
          between sections. Press <PanelSurface.InlineCode>?</PanelSurface.InlineCode> for the full
          keyboard reference.
        </PanelSurface.Prose>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
